import React, { useMemo } from 'react';
import { DrcTooltip } from '@driscollsinc/driscolls-react-components';
import { makeStyles } from '@material-ui/core/styles';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import GridStyles from '../styles/gridStyles';

const useStyles = makeStyles((theme) => ({
    input: {
        '& .MuiOutlinedInput-input': {
            textAlign: 'left'
        }
    },
    gridStyles: GridStyles.styles(theme, 'calc(100vh -280px)'),
    dialogWrapper: {
        '& .MuiDialog-paperWidthSm': {
            overflow: 'hidden',
            maxWidth: '900px !important', // To override dialog maxWidth
            minHeight: '300px',
            maxHeight: '70vh'
        }
    },
    asyncItem: {
        height: '45px',
        width: '100%',
        alignSelf: 'center',
        marginTop: '1px'
    },
    deleteButton: {
        minWidth: '25px',
        maxWidth: '25px',
        margin: '4px 8px 8px 0',
        color: '#e34843',
        border: 'none',
        '&:hover': {
            border: 'none',
            backgroundColor: '#ffffff00',
            color: '#ec110a'
        },
        [theme.darkTheme]: {
            backgroundColor: '#0000',
            '&:hover': {
                border: 'none',
                color: '#ec110a',
                backgroundColor: '#0000'
            }
        }
    }
}));

// ---- stable references to avoid re-renders ----
const NOOP = () => null;
const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

const PrimeReactTable = (props) => {
    const classes = useStyles();

    const {
        value = EMPTY_ARRAY,
        editMode = 'row',
        frozenWidth = null,
        paginator = false,
        currentPage = 0,
        onPage = NOOP,
        totalRecords = 0,
        uniqueKeyField = null,
        editingRows = EMPTY_OBJECT,
        columns = EMPTY_ARRAY,
        selectionMode,
        selected = EMPTY_ARRAY,
        rowSelect = false,
        selectedFrozen = false,
        tableSelectionMode,
        selectedClass = {}, // if you use it later
        selectionChange = NOOP,
        onRowEditInit = NOOP,
        onRowEditCancel = NOOP,
        onRowClick = NOOP,
        pageSize = 10,
        editor = NOOP,
        emptyRowsMessage = 'No data to show',
        columnResizeMode = 'expand',
        rowClassName = null,
        resizableColumns = true
    } = props;

    // memoized derived values
    const heightAdjusted = useMemo(() => {
        const calculatedHeight = 60 * value.length;
        const baseHeight = 500;
        const finalHeight = calculatedHeight < baseHeight ? calculatedHeight : baseHeight;
        return `${finalHeight}px`;
    }, [value.length]);

    const isPaginatorActive = useMemo(() => paginator && totalRecords > 0, [paginator, totalRecords]);

    return (
        <div
            className={classes.gridStyles}
            style={{
                width: '100%'
            }}
        >
            <DataTable
                value={value}
                lazy
                editMode={editMode}
                frozenWidth={frozenWidth}
                onRowEditInit={onRowEditInit}
                onRowEditCancel={onRowEditCancel}
                paginator={isPaginatorActive}
                first={currentPage}
                onRowClick={onRowClick}
                rows={pageSize}
                onPage={onPage}
                emptyMessage={emptyRowsMessage}
                totalRecords={totalRecords}
                columnResizeMode={columnResizeMode}
                rowClassName={rowClassName}
                resizableColumns={resizableColumns}
                virtualRowHeight={28}
                scrollable
                {...(tableSelectionMode ? { selectionMode: tableSelectionMode } : {})}
                responsiveLayout="scroll"
                dataKey={uniqueKeyField}
                editingRows={editingRows}
                onRowEditChange={NOOP}
                onRowEditComplete={NOOP}
                scrollHeight={heightAdjusted}
                scrollWidth="100%"
                style={{ height: 'auto' }}
                selection={selected}
                onSelectionChange={selectionChange}
                currentPageReportTemplate={'Showing {first} to {last} of {totalRecords} entries'}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
            >
                {rowSelect && (
                    <Column
                        headerStyle={{ width: '3em' }}
                        selectionMode={selectionMode || 'multiple'}
                        frozen={selectedFrozen}
                        className={selectedClass}
                    />
                )}

                {columns.map((column, indx) => (
                    <Column
                        key={column.key || indx}
                        frozen={column.frozen || false}
                        editable={column.editable}
                        style={{
                            width: column.width || '150px',
                            paddingLeft: 8
                        }}
                        field={column.key}
                        header={
                            <DrcTooltip tipText={column.name}>
                                <span>{column.name}</span>
                            </DrcTooltip>
                        }
                        bodyStyle={{
                            ...(indx ? { paddingLeft: 10 } : { paddingLeft: 6 })
                        }}
                        body={column.columnTemplate || null}
                        {...(column.editable
                            ? {
                                  editor: (props) => editor(column.key, column, props)
                              }
                            : {})}
                    />
                ))}
            </DataTable>
        </div>
    );
};

// prevent unnecessary re-renders when props don’t change
export default React.memo(PrimeReactTable);
